/**
 * V2 client-side types + React Query wiring for the unified brand app
 * bootstrap endpoint (`GET /api-rewards/brandapp-v2/bootstrap`).
 *
 * The wire shape mirrored here is the hardened "right view"
 * `BootstrapResponseV2` produced by `BrandAppV2Controller.bootstrap` on the
 * server. Server-side schema (Zod) lives at:
 *   packages/api-rewards/src/api/controllers/brandAppV2/projections/
 *
 * This file only defines TypeScript mirrors (no Zod) because the client trusts
 * the server's egress validation. Keep these types in lockstep with the
 * server projections; if you add a new field on the server, add it here too.
 */

import { queryOptions, useQuery } from '@tanstack/react-query';
import { getBootstrapShared, THIRTY_MINUTES_IN_MS, type BCQueryOptions } from './appConfigQueryOptions';

// ── BootstrapResponseV2 wire-format mirrors ──

export interface AppPublicV2 {
  appId: string;
  appType: string;
  appName: string;
  description?: string;
  brandIds: number[];
  brandclubIds: number[];
  retailerIds: number[];
  /**
   * Server-computed canonical concierge URL for the brand's chosen serving
   * surface (Plan 6 / D-23). Optional: absent until the server bootstrap
   * projection exposes it; consumers fall back to same-origin behavior.
   */
  canonicalConciergeUrl?: string;
  /**
   * The brand's own Brandclub consumer origin. Brandclub member pages (`/me`,
   * `/purchase`) live here and NOT under the Shopify App Proxy path, so a
   * `stackline` app served on Shopify must link to them absolutely (D-42.10).
   */
  consumerBaseUrl?: string;
  /**
   * The brand's chosen consumer serving surface (Plan 7 Part A). Absent ⇒
   * `shopperos_domain` semantics (pre-switch default).
   */
  servingSurface?: 'shopperos_domain' | 'shopify_app_proxy';
  /**
   * The word shoppers see for the bag, set on Settings → Checkout. Absent on an
   * older server. Read it through `useBagWords`, which gives "bag" then.
   */
  bagNoun?: 'bag' | 'cart' | 'basket' | 'trolley';
  /**
   * The app's sales channels, as the server sends them. A `shopify` channel's
   * retailer tells the front room which product urls point at the merchant's own
   * store. Optional so an older server still type-checks.
   */
  channels?: Array<{
    appChannelId: string;
    channelType: string;
    role?: string;
    status?: string;
    channelRetailerId?: number;
    shopDomain?: string;
    /** Shopify only. The host that serves this store's cart, when an operator set it. */
    storefrontCartHost?: string;
  }>;
}

export interface BrandIdentityPublicV2 {
  brandId: number;
  brandName: string;
  storeName?: string;
  storeDescription?: string;
  storeBrandingType?: string;
  customShopDomainName?: string;
  alternateCustomShopDomainNames?: string[];
  customShopPageUrls?: Record<string, string>;
  mobileAppLink?: string;
}

export interface IdentityPublicV2 {
  hostname: string;
  aliases: string[];
  brand: BrandIdentityPublicV2;
}

export interface CognitoAuthPublicV2 {
  userPoolId: string;
  /**
   * Resolved Cognito user pool client id for the caller's surface
   * (`web` for browsers, `mobile` for native). Picked server-side
   * from the binding's per-surface map so clients hand it straight
   * to Amplify without any selection logic.
   */
  userPoolClientId: string;
  identityPoolId: string;
  region: string;
  signInDomain: string;
}

/**
 * How this app authenticates shoppers (D-42). Mirrors the server's
 * `BrandAppAuthTypeV2`; keep the two in lockstep.
 */
export type AuthTypeV2 = 'stackline' | 'shopify' | 'no_auth';

export interface AuthPublicV2 {
  /**
   * Always present. Branch on this, not on `cognito` being absent — absence is
   * ambiguous (an `api`-surface caller looks the same as a guest-only app).
   */
  authType: AuthTypeV2;
  /** Where the "Purchases" link points. The server decides it. Older servers omit it. */
  purchasesPage?: 'brandclub' | 'shopify' | 'none';
  /** Present only for `stackline` — it is what Amplify needs. */
  cognito?: CognitoAuthPublicV2;
}

export interface CheckoutCapabilityRowV2 {
  enabled: true;
  platform?: 'shopify' | 'magento' | 'salesforce' | 'custom';
  shopDomain?: string;
  retailerId: number;
  allowedOfferType?: 'all' | 'subscription' | 'one_time';
}

/** Keyed by `appChannelId`. */
export type CheckoutCapabilityPublicV2 = Record<string, CheckoutCapabilityRowV2>;

/** `enabled: false` means an operator turned the concierge off in Settings. */
export interface ConciergeCapabilityPublicV2 {
  enabled: boolean;
}

export interface DistributionCapabilityPublicV2 {
  appSlug: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}

export interface DeepLinkingCapabilityPublicV2 {
  provider: 'branch';
  apiKey: string;
  branchAppId: string;
  mobileAppBranchLink?: string;
  webAppBranchLink?: string;
}

/**
 * Where-to-buy capability — operator-curated list of retailers whose
 * product CTAs deep-link to the retailer's own product detail page.
 * The server pre-filters retailers without a usable
 * `retailerProductPageUrlFormat`, so consumers can render
 * unconditionally for every retailerId in this list.
 */
export interface WhereToBuyCapabilityPublicV2 {
  retailerIds: number[];
}

/**
 * Store-locator capability — list of retailers with a parsed,
 * operator-uploaded store list ready to render. Counts and
 * `lastParsedAt` are echoed so consumers can render the
 * "1,820 stores · refreshed 2 days ago" strip without a follow-up
 * call. The server only emits retailers whose underlying
 * `store_locator_stores` binding is `parseStatus === 'ready'`.
 */
export interface StoreLocatorRetailerPublicV2 {
  retailerId: number;
  storeCount?: number;
  lastParsedAt?: number;
}
export interface StoreLocatorCapabilityPublicV2 {
  retailers: StoreLocatorRetailerPublicV2[];
}

export interface CapabilitiesPublicV2 {
  checkout?: CheckoutCapabilityPublicV2;
  concierge?: ConciergeCapabilityPublicV2;
  distribution?: DistributionCapabilityPublicV2;
  deepLinking?: DeepLinkingCapabilityPublicV2;
  whereToBuy?: WhereToBuyCapabilityPublicV2;
  storeLocator?: StoreLocatorCapabilityPublicV2;
}

export interface ThemeEntryPublicV2 {
  scope: string;
  version: string;
  schemaVersion: 1;
  data: unknown;
}

export type ThemesPublicV2 = Record<string, ThemeEntryPublicV2>;

export interface PixelPublicV2 {
  pixelId: string;
  provider?: string;
  eventMapping?: Record<string, unknown>;
}

export interface AbTestingResultPublicV2 {
  treatment: string;
  config: string;
}

export interface TrackingPublicV2 {
  pixels: PixelPublicV2[];
  abTesting: Record<string, AbTestingResultPublicV2>;
  lduAffectedStates: string[];
}

/**
 * The page head: tab title, tab icon, link-preview values and header logo. The server
 * decides them (api-common `resolveAppHead`), so a client only applies them.
 */
export interface HeadPublicV2 {
  siteName: string;
  /** The tab title for a labelled page. `%s` is the page label. */
  titleTemplate: string;
  description: string;
  iconUrl?: string;
  shareImageUrl?: string;
  /** The logo in the app header. */
  logoUrl?: string;
  themeColor?: string;
  /** First path segment → page label, e.g. `mybag` → "My Bag". */
  pageLabels: Record<string, string>;
}

/**
 * The app's module settings, every option resolved by the server. pages-runtime `contract/moduleSettings.ts` owns
 * the shape and the defaults. This copy is typed by hand, as the rest of the bootstrap is.
 */
export interface ModuleSettingsPublicV2 {
  checkout?: {
    storeLocator?: {
      /** Whether the store locator shows its "Purchase online" row. */
      purchaseOnline?: boolean;
    };
  };
}

/** Response shape for `GET /api-rewards/brandapp-v2/bootstrap`. */
export interface AppBootstrapV2 {
  apiVersion: '1';
  app: AppPublicV2;
  identity: IdentityPublicV2;
  auth: AuthPublicV2;
  capabilities: CapabilitiesPublicV2;
  themes: ThemesPublicV2;
  tracking: TrackingPublicV2;
  /** Absent on a response cached before the server sent it. */
  head?: HeadPublicV2;
  /** Absent when the server could not read the Modules config, and on a response cached before the field. */
  moduleSettings?: ModuleSettingsPublicV2;
}

// ── React Query wiring ──

/**
 * Namespaced V2 cache keys. Kept separate from V1's `CachedQueryKeys` enum
 * so the V1 file stays untouched.
 */
export const V2CachedQueryKeys = {
  AppBootstrapV2: 'appBootstrapV2',
} as const;

// Delegate to the shared module-level fetcher so V1-adapter callers and
// direct V2 callers go through one set of headers (auth + X-Client-AppId)
// and share the in-flight dedupe promise instead of racing to the same
// endpoint with divergent headers.
const getAppBootstrapV2 = (signal?: AbortSignal, apiBaseUrl?: string): Promise<AppBootstrapV2> =>
  getBootstrapShared(signal, apiBaseUrl);

export const appBootstrapV2QueryOptions = (apiBaseUrl?: string) => {
  return queryOptions({
    queryKey: [V2CachedQueryKeys.AppBootstrapV2],
    queryFn: ({ signal }) => getAppBootstrapV2(signal, apiBaseUrl),
    staleTime: THIRTY_MINUTES_IN_MS,
  });
};

export const useAppBootstrapV2 = <TSelect = AppBootstrapV2>(
  apiBaseUrl?: string,
  args?: BCQueryOptions<typeof appBootstrapV2QueryOptions, AppBootstrapV2, TSelect>,
) => {
  return useQuery({
    ...appBootstrapV2QueryOptions(apiBaseUrl),
    ...args,
    select: args?.select,
  });
};
